import { createFileRoute } from "@tanstack/react-router";
import { Section } from "@/components/layout";
import ogImageAsset from "@/assets/og-tb-logo-navy.png.asset.json";
const ogImage = `https://tablesandbridges.com${ogImageAsset.url}`;

const TITLE = "Privacy Policy | Tables and Bridges";
const DESC =
  "How Tables and Bridges collects, uses, and protects information from visitors, members, and agency partners.";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://tablesandbridges.com/privacy" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: ogImage },
      { property: "og:image:alt", content: "Tables and Bridges logo - a navy and green circular emblem of a table framed by a bridge arch, above the wordmark Tables and Bridges and the tagline: Building Trust. Connecting Communities. Creating Impact." },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: ogImage },
      { name: "twitter:image:alt", content: "Tables and Bridges logo - a navy and green circular emblem of a table framed by a bridge arch, above the wordmark Tables and Bridges and the tagline: Building Trust. Connecting Communities. Creating Impact." },
    ],
    links: [{ rel: "canonical", href: "https://tablesandbridges.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div>
      <section className="bg-navy text-warm-white">
        <div className="container-editorial py-20 md:py-28">
          <p className="eyebrow text-gold mb-6">Fine print</p>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl">
            Privacy Policy
          </h1>
          <p className="mt-6 text-warm-white/75 max-w-2xl">
            This site is being prepared for launch. Full legal copy will be published before
            public release. The summary below describes our intended practices.
          </p>
        </div>
      </section>

      <Section>
        <div className="container-editorial max-w-3xl space-y-10 text-soft-black/85 leading-relaxed">
          <Block title="Information we collect">
            We collect information you provide directly through forms on this site, such as your
            name, email address, agency or organization, and the content of your message. We may
            also collect standard technical information (browser type, device, referring page)
            through server logs and privacy-respecting analytics.
          </Block>
          <Block title="How we use information">
            We use submitted information to respond to inquiries, evaluate applications, deliver
            requested resources, coordinate speaking or training engagements, and send updates
            you have opted into. We do not sell personal information.
          </Block>
          <Block title="Email and communications">
            When you join a waitlist or request contact, we may add you to a mailing list managed
            through our email service provider. You may unsubscribe at any time using the link in
            any message.
          </Block>
          <Block title="Data sharing">
            We share information only with service providers that help us operate this site and
            communicate with you, and only as required by law. Agency-specific information shared
            with us during engagements is treated as confidential.
          </Block>
          <Block title="Your choices">
            You may request access to, correction of, or deletion of personal information you
            have provided by contacting us at the address below.
          </Block>
          <Block title="Contact">
            For privacy questions, contact Tables and Bridges through the{" "}
            <a href="/book-carlai" className="text-navy underline hover:text-gold">
              contact form
            </a>
            .
          </Block>
          <p className="text-sm text-soft-black/60">Last updated: 2026</p>
        </div>
      </Section>
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-display text-2xl md:text-3xl text-navy mb-3">{title}</h2>
      <div>{children}</div>
    </div>
  );
}
